import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import * as turf from "@turf/turf"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import Select from "react-select"
import "react-dates/initialize"
import { Loading, Modal, Tab, Tabs, Input } from "@wfp/ui"
import { SingleDatePicker } from "react-dates"
import { Moment } from "moment"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import Button from "components/Shared/Button/Button"
import { useNavigate } from "react-router-dom"
import useCommon from "hooks/useCommon"
import AlertComponent from "components/Shared/Common/Alert"
import { GeoJsonBoundries, SearchResponse, SimpleSelect, SurveyData, XlsForm } from "interfaces/dataInterfaces"
import { getJsonFromXlsx } from "utils/getJsonFromXlsx"
import { renderMonthElement } from "utils/dateHelper"
import { useLazyGetGeoJsonBoundariesQuery } from "services/geoData/geoJson"
import UploadCommodity from "../CommoditiesPages/UploadCommodity"
import { ISurveyData } from "./SurveyRPME"

interface IAddSurveyPage {
  data: Partial<ISurveyData>
  setData: Dispatch<SetStateAction<Partial<ISurveyData>>>
  xlsForms?: SearchResponse<XlsForm>
  surveyTypeId: number
  isFileSaving: boolean
  isValidateFetching: boolean
  validateSurveyRes: any
  isValidateMinFetching: boolean
  isSaving: boolean
  isSuccess: boolean
  validateSurveyMin:
    | {
        isValid: boolean
        validationResult: string
        validationToken: string
      }
    | undefined
  saveCSVFile: (data: FormData) => void
  saveSurvey: (data: SurveyData) => Promise<any>
  surveyData?: { isSuccess: boolean; resultMessage: string; surveyId: number }
}

export default function AddSurvey({
  data,
  xlsForms,
  setData,
  surveyTypeId,
  isFileSaving,
  validateSurveyRes,
  isValidateFetching,
  isValidateMinFetching,
  validateSurveyMin,
  saveCSVFile,
  saveSurvey,
  isSaving,
  isSuccess,
  surveyData,
}: IAddSurveyPage) {
  const navigate = useNavigate()
  const { showSnackBar, showAlert, hideAlert } = useCommon()

  const [uploadCommodity, setUploadCommodity] = useState(false)
  const [adm0code, setAdm0code] = useState<number | undefined>(undefined)
  const [xlsFormId, setXlsFormId] = useState<number | undefined>(undefined)
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [file, setFile] = useState<File | undefined>(undefined)
  const [focused, setFocused] = useState({ startDate: false, endDate: false })
  const [errors, setErrors] = useState<string[]>([])

  const [getGeoJsonBoundaries, { data: geoJsonBoundary, isLoading: isGeoJsonLoading }] =
    useLazyGetGeoJsonBoundariesQuery()

  const [validateSurvey, setValidateSurvey] = useState(validateSurveyRes)
  const [options, setOptions] = useState<{ xlsForms: SimpleSelect[] }>({ xlsForms: [] })

  const handleNavigate = () => {
    setTimeout(() => {
      navigate(window.location.pathname?.replace("/add", ""))
    }, 1000)
  }

  const handleClear = () => {
    hideAlert()
    setValidateSurvey(undefined)
  }

  const handleSave = async () => {
    if (validateSurveyMin && validateSurvey && adm0code !== -1) {
      handleClear()
      const response = await saveSurvey({
        adm0code: adm0code ?? 0,
        surveyEndDate: endDate?.toISOString() ?? null,
        surveyStartDate: startDate?.toISOString() ?? null,
        surveyValidationReport: validateSurvey?.validationResult ?? "",
        validationToken: validateSurveyMin.validationToken ?? "",
        xlsFormId: xlsFormId ?? 0,
        surveyTypeId,
        surveyOriginalFilename: "",
      })

      if (!response?.isSuccess) {
        showSnackBar(response?.resultMessage, "error")
        return
      }

      showSnackBar("Survey added successfully!")

      if (surveyTypeId === 1) {
        handleNavigate()
      } else {
        setUploadCommodity(true)
      }
    }
  }

  const uploadFile = useCallback(() => {
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)
    saveCSVFile(formData)
  }, [file])

  useEffect(() => {
    setValidateSurvey(validateSurveyRes)
    if (validateSurveyRes?.isValid && validateSurveyMin?.isValid) {
      showAlert("Data contained in the CSV file is valid and can be saved in the database.")
    } else {
      showAlert(
        "Invalid data. Please review the above issue(s) and modify the CSV file in order to be compliant with the XLSForm schema",
        "error",
      )
    }
  }, [validateSurveyRes, validateSurveyMin])

  useEffect(() => {
    if (file) {
      // if it's not RPME module then don't validate for coordinates just upload the file directly
      if (surveyTypeId !== 2) {
        uploadFile()
        return
      }

      getJsonFromXlsx(file).then(sheets => {
        const { data: sheetData } = sheets[0]
        const geoColumnIndex: number = sheetData[0].findIndex(item => item.value === "SHGps") // get index from first row which is header
        const surveyModalityColumnIndex: number = sheetData[0].findIndex(item => item.value === "RPMESvyMod")
        const trdNameColumnIndex: number = sheetData[0].findIndex(item => item.value === "TrdName") // get index from first row which is header
        const tempErrors: string[] = []

        if (geoColumnIndex > -1 && surveyModalityColumnIndex > -1) {
          sheetData?.shift() // remove header row from data
          sheetData?.forEach((item: any, index: number) => {
            const surveyModalityValue = item[surveyModalityColumnIndex]?.value
            if (surveyModalityValue !== 1) {
              return
            }
            const geoValues = item[geoColumnIndex]?.value
            const trdName = trdNameColumnIndex ? ` (${item[trdNameColumnIndex]?.value})` : ""
            const [long, lat] = geoValues.split(" ").filter((el: string) => el)

            // eslint-disable-next-line no-restricted-globals
            if (!geoValues || isNaN(lat) || isNaN(long)) {
              tempErrors.push(`<b>Row : ${index + 1}</b>${trdName} Validation failed for value: <b> ${geoValues} </b>`)
              return
            }

            const isInside = turf.booleanPointInPolygon(
              turf.point([lat, long]),
              (geoJsonBoundary as GeoJsonBoundries).features[0],
            )
            if (!isInside)
              tempErrors.push(
                `<b>Row : ${
                  index + 1
                }</b>${trdName} Value is not inside selected country boundary: <b>${lat} ${long}</b>`,
              )
          })
        }

        if (!tempErrors.length) uploadFile()
        setErrors(tempErrors)
      })
    }
  }, [file])

  useEffect(() => {
    if (!xlsForms?.items.length) {
      setOptions({ xlsForms: [] })
      return
    }
    const xslOptions = xlsForms?.items.map((item: XlsForm) => ({ value: item.xlsFormId, label: item.xlsFormName }))
    setOptions({ xlsForms: xslOptions })
  }, [xlsForms?.items])

  useEffect(() => {
    if (!adm0code) return
    getGeoJsonBoundaries({ adm0code })
  }, [adm0code])

  useEffect(() => {
    return () => {
      handleClear()
    }
  }, [])

  return (
    <>
      {!uploadCommodity && (
        <div className="max-w-4xl relative">
          <div className="space-y-6">
            <div>
              <CountrySelector
                allowAll={false}
                isClearable={false}
                label="Country"
                onSelect={country => {
                  setAdm0code(country?.value)
                  setXlsFormId(undefined) // reset XLSForm selection
                  setData({ ...data, addAdmCode: country?.value, xlsFormId: undefined })
                  setOptions({ xlsForms: [] })
                }}
              />
            </div>

            <div>
              <label className="wfp--label text-xl">XLSForm Schema</label>
              <Select
                key={xlsFormId}
                placeholder="Select an XLSForm"
                classNamePrefix="select"
                value={options.xlsForms?.find(item => item.value === xlsFormId)}
                options={options.xlsForms}
                onChange={(event: any) => {
                  setXlsFormId(event?.value)
                  setData({ ...data, xlsFormId: event?.value })
                }}
              />
            </div>

            <div className="flex">
              <Input labelText="Start Date" invalidText="A valid value is required">
                {() => (
                  <SingleDatePicker
                    showDefaultInputIcon
                    renderMonthElement={renderMonthElement}
                    inputIconPosition="after"
                    onDateChange={newDate => setStartDate(newDate)}
                    focused={focused.startDate}
                    onFocusChange={({ focused: dateSelected }) =>
                      setFocused({ startDate: dateSelected, endDate: false })
                    }
                    date={startDate}
                    id="start_date"
                    isOutsideRange={m => {
                      if (!endDate) return false
                      return m.unix() > endDate.unix()
                    }}
                  />
                )}
              </Input>
              <Input invalidText="A valid value is required" labelText="End Date">
                {() => (
                  <SingleDatePicker
                    showDefaultInputIcon
                    renderMonthElement={renderMonthElement}
                    inputIconPosition="after"
                    onDateChange={newDate => setEndDate(newDate)}
                    focused={focused.endDate}
                    onFocusChange={({ focused: dateSelected }) =>
                      setFocused({ startDate: false, endDate: dateSelected })
                    }
                    date={endDate}
                    id="end_date"
                    isOutsideRange={m => {
                      if (!startDate) return false
                      return m.unix() < startDate.unix()
                    }}
                  />
                )}
              </Input>
            </div>

            {(isFileSaving || isSaving || isValidateFetching || isValidateMinFetching || isGeoJsonLoading) && (
              <Loading withOverlay={false} />
            )}
            {!isFileSaving && !isSaving && !isValidateFetching && !validateSurvey && geoJsonBoundary && (
              <ExcelUploader
                onSelect={setFile}
                placeholder="Select the CSV containing the survey responses"
                subPlaceholder={
                  surveyTypeId === 2
                    ? "After the survey validation you will be asked to upload the commodity basket CSV file"
                    : ""
                }
              />
            )}
          </div>

          {validateSurvey && validateSurveyMin && (
            <div className="mt-12">
              <Tabs>
                <Tab label="Validation" href="" role="tab" selected tabIndex={0}>
                  <div className="h-32 border-gray-300 border-solid border p-1.5 text-sm overflow-x-auto">
                    <pre>{validateSurvey.validationResult}</pre>
                    <hr />
                    <pre>{validateSurveyMin.validationResult}</pre>
                  </div>
                  <br />
                  <AlertComponent />
                </Tab>
              </Tabs>
            </div>
          )}

          {validateSurvey?.isValid && validateSurveyMin?.isValid && !isSaving && (
            <div>
              <Button action={handleSave} label="Save" className="btn-full blue mr-1" />
            </div>
          )}

          {validateSurvey && (!validateSurvey?.isValid || !validateSurveyMin?.isValid) && (
            <Button action={handleClear} label="Clear & Re-Upload" className="btn-full blue" />
          )}

          <Modal
            open={isSuccess}
            modalHeading="File successfully saved"
            primaryButtonText="Close"
            onRequestSubmit={handleNavigate}
            onRequestClose={handleNavigate}
          />
        </div>
      )}

      {surveyTypeId === 2 && (
        <Modal
          open={errors.length > 0}
          modalHeading="Coordinates Validation"
          secondaryButtonText="Close"
          primaryButtonText="Proceed with errors"
          onRequestSubmit={() => {
            setErrors([])
            uploadFile()
          }}
          onRequestClose={() => setErrors([])}
        >
          <ul className="max-h-96 overflow-auto list-disc mb-1 leading-6 list-inside pl-6">
            {errors.map((error, index) => (
              // eslint-disable-next-line react/no-danger
              <li key={error + index} dangerouslySetInnerHTML={{ __html: error }} />
            ))}
          </ul>
        </Modal>
      )}

      {uploadCommodity && surveyTypeId === 2 && surveyData?.surveyId && (
        <UploadCommodity surveyId={surveyData.surveyId} surveyTypeId={surveyTypeId} onSave={handleNavigate} />
      )}
    </>
  )
}
